import { TeamlessCollaborationCreateTeamMembers } from './TeamlessCollaborationCreateTeamMembers';
import React, { useState } from 'react';
import { forNamespace } from 'app/common/lib/util/i18n';
import styles from './TeamlessCollaborationCreateTeam.less';
import {
  useTeamlessCollaborationCreateTeamAddMembersMutation,
  useTeamlessCollaborationCreateTeamSubmitMutation,
  useTeamfulCollaborationMoveBoardToOrgMutation,
  useTeamlessCollaborationCreateTeamMembersQuery,
} from '@trello/graphql/generated';
import { useAllUsersAreAdminsEnabled } from '@trello/organizations';
import {
  Button,
  ButtonSizes,
  ButtonTypes,
} from 'app/common/components/controls/button/button';
import Alerts from 'app/scripts/views/lib/alerts.coffee';
import { Spinner } from 'app/src/components/Spinner';
import { Noun, Verb, trackUe } from '@trello/analytics';
import { TeamTypeSelect } from 'app/src/components/TeamTypeSelect';

const format = forNamespace('teamless collaboration prompt');

const noop = () => {};

interface TeamlessCollaborationCreateTeamProps {
  boardId: string;
  onTeamCreated: (orgName: string) => void;
}

export const TeamlessCollaborationCreateTeam: React.FunctionComponent<TeamlessCollaborationCreateTeamProps> = ({
  boardId,
  onTeamCreated,
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [teamType, setTeamType] = useState();
  const [isTeamNameValid, setIsTeamNameValid] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);

  const allAdminsEnabled = useAllUsersAreAdminsEnabled();

  const [
    createOrganization,
  ] = useTeamlessCollaborationCreateTeamSubmitMutation();

  const [
    addMembersToOrg,
  ] = useTeamlessCollaborationCreateTeamAddMembersMutation();

  const [
    moveBoardToOrgMutation,
  ] = useTeamfulCollaborationMoveBoardToOrgMutation();

  const { data } = useTeamlessCollaborationCreateTeamMembersQuery({
    variables: { boardId },
  });
  const allMembers = data?.board?.members || [];

  const onChangeTeamName: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const newTeamName = e.target.value;
    setTeamName(newTeamName);
    setIsTeamNameValid(newTeamName.trim().length > 0);
  };

  const onCreateTeamSubmit: React.FormEventHandler = async (e) => {
    e.preventDefault();
    setIsSubmitted(true);

    try {
      // 1. Create Team
      const createResponse = await createOrganization({
        variables: {
          type: 'default',
          displayName: teamName.trim(),
          teamType,
          allAdminsEnabled,
        },
      });
      const orgName = createResponse.data?.createOrganization?.name;
      const orgId = createResponse.data?.createOrganization?.id;

      if (orgId) {
        // 2. Add the current board to the new team.
        await moveBoardToOrgMutation({
          variables: { boardId, orgId },
        });

        // 3. Add the chosen members to the team
        await addMembersToOrg({
          variables: {
            orgId,
            users: selectedMembers.map((idMember) => ({ id: idMember })),
          },
        });
      }

      trackUe({
        category: Noun.BOARD_VIEW,
        verb: Verb.SUBMITS,
        directObj: Noun.TEAMLESS_COLLABORATION_PROMPT,
        method: 'by clicking the create team button',
        context: {
          boardId,
          didExcludeMembers: selectedMembers.length !== allMembers.length,
          membersCount: selectedMembers.length,
        },
      });

      if (orgName) {
        onTeamCreated(orgName);
      }
    } catch (err) {
      Alerts.showLiteralText(err.message, 'error', 'alert', 5000);
      setIsSubmitted(false);
    }
  };

  const isButtonEnabled = isTeamNameValid && !!teamType && !isSubmitted;

  return (
    <div className={styles.createTeam}>
      <label
        className={styles.createTeamInputLabel}
        htmlFor="teamlessCollaborationCreateTeamInput"
      >
        {format(['create team form', 'input label'])}
      </label>
      <input
        id="teamlessCollaborationCreateTeamInput"
        className={styles.createTeamInput}
        type="text"
        autoComplete="off"
        spellCheck={false}
        onChange={onChangeTeamName}
        autoFocus={true}
        placeholder={format(['create team form', 'input placeholder'])}
        value={teamName}
        maxLength={100}
        disabled={isSubmitted}
      />
      <TeamTypeSelect
        onChange={(teamTypeValue) => setTeamType(teamTypeValue)}
        isDisabled={isSubmitted}
      />
      <TeamlessCollaborationCreateTeamMembers
        boardId={boardId}
        onSelectMember={(currentSelectedMembers) =>
          setSelectedMembers(currentSelectedMembers)
        }
      />
      <Button
        buttonType={
          isButtonEnabled ? ButtonTypes.PRIMARY : ButtonTypes.DISABLED
        }
        className={styles.createTeamButton}
        onClick={isButtonEnabled ? onCreateTeamSubmit : noop}
        size={ButtonSizes.FULLWIDTH}
      >
        {isSubmitted ? (
          <Spinner centered />
        ) : (
          format(['create team form', 'create team'])
        )}
      </Button>
    </div>
  );
};
